import React from 'react';
import { FileTextIcon, MailIcon, MapPinIcon } from 'lucide-react';
const sections = [{
  id: 'agreement',
  label: 'Agreement to Terms'
}, {
  id: 'ip',
  label: 'Intellectual Property Rights'
}, {
  id: 'user-reps',
  label: 'User Representations'
}, {
  id: 'prohibited',
  label: 'Prohibited Activities'
}, {
  id: 'user-content',
  label: 'User Generated Contributions'
}, {
  id: 'submissions',
  label: 'Submissions'
}, {
  id: 'management',
  label: 'Site Management'
}, {
  id: 'privacy',
  label: 'Privacy Policy'
}, {
  id: 'sms',
  label: 'SMS Communications Terms'
}, {
  id: 'email',
  label: 'Email Communications Terms'
}, {
  id: 'term',
  label: 'Term and Termination'
}, {
  id: 'disclaimer',
  label: 'Disclaimer'
}, {
  id: 'liability',
  label: 'Limitations of Liability'
}, {
  id: 'indemnification',
  label: 'Indemnification'
}, {
  id: 'law',
  label: 'Governing Law'
}, {
  id: 'arbitration',
  label: 'Dispute Resolution / Arbitration'
}, {
  id: 'contact',
  label: 'Contact Us'
}];
interface TermsOfUseProps {
  onNavigate?: (path: string) => void;
}
export function TermsOfUse({
  onNavigate
}: TermsOfUseProps) {
  const lastUpdated = 'May 20, 2026';
  return <div className="min-h-screen bg-white" data-id="element-3204">
      {/* Header */}
      <header className="bg-slate-50 border-b border-slate-200" data-id="element-3205">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-id="element-3206">
          <div className="flex items-center gap-3 mb-4" data-id="element-3207">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center" data-id="element-3208">
              <FileTextIcon className="w-5 h-5 text-amber-600" data-id="element-3209" />
            </div>
            <span className="text-sm font-semibold text-amber-600 uppercase tracking-wider" data-id="element-3210">
              Legal
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3 tracking-tight" data-id="element-3211">
            Terms of Service
          </h1>
          <p className="text-slate-600" data-id="element-3212">
            Last updated:{' '}
            <span className="font-medium text-slate-700" data-id="element-3213">{lastUpdated}</span>
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-id="element-3214">
        {/* Table of contents */}
        <section className="mb-12 bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8" data-id="element-3215">
          <h2 className="text-xl font-bold text-slate-900 mb-4 uppercase tracking-wider" data-id="element-3216">
            Table of Contents
          </h2>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 list-decimal list-inside" data-id="element-3217">
            {sections.map(s => <li key={s.id} className="text-slate-700" data-id="element-3218">
                <a href={`#${s.id}`} className="hover:text-amber-600 hover:underline transition-colors" data-id="element-3219">
                  {s.label}
                </a>
              </li>)}
          </ol>
        </section>

        {/* Section 1 */}
        <section id="agreement" className="scroll-mt-24" data-id="element-3220">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3221">
            Agreement to Terms
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-3222">
            These Terms of Use constitute a legally binding agreement between
            you and <strong data-id="element-3223">findservicequotes.io</strong> concerning your access
            to and use of the website. By accessing the site, you confirm that
            you have read, understood, and agree to be bound by these Terms of
            Use.{' '}
            <strong className="uppercase" data-id="element-3224">
              If you do not agree with all of these Terms of Use, you are
              prohibited from using the site and must discontinue use
              immediately.
            </strong>
          </p>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-3225">
            We reserve the right to make changes or modifications to these Terms
            of Use at any time and for any reason. We will alert you to any
            changes by updating the &ldquo;Last updated&rdquo; date above. Your
            continued use of the site after revised terms are posted constitutes
            acceptance of the changes.
          </p>
          <p className="text-slate-700 leading-relaxed" data-id="element-3226">
            The site is intended for users who are at least 18 years old.
            Persons under 18 are not permitted to use or register for the site.
          </p>
        </section>

        {/* Section 2 */}
        <section id="ip" className="mt-14 scroll-mt-24" data-id="element-3227">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3228">
            Intellectual Property Rights
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-3229">
            Unless otherwise indicated, the site is our proprietary property,
            and all source code, databases, functionality, software, website
            designs, audio, video, text, photographs, and graphics on the site
            are owned or controlled by us or licensed to us, and are protected
            by copyright and trademark laws.
          </p>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-3230">
            The content is provided &ldquo;AS IS&rdquo; for your information and
            personal use only. Except as expressly provided in these Terms of
            Use, no part of the site or content may be copied, reproduced,
            aggregated, republished, uploaded, posted, publicly displayed,
            encoded, translated, transmitted, distributed, sold, licensed, or
            otherwise exploited for any commercial purpose without our express
            prior written permission.
          </p>
          <p className="text-slate-700 leading-relaxed" data-id="element-3231">
            You are granted a limited license to access and use the site and to
            download or print a copy of any portion of the content to which you
            have properly gained access solely for your personal, non-commercial
            use.
          </p>
        </section>

        {/* Section 3 */}
        <section id="user-reps" className="mt-14 scroll-mt-24" data-id="element-3232">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3233">
            User Representations
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-3234">
            By using the site, you represent and warrant that:
          </p>
          <ul className="text-slate-700 space-y-2 list-disc list-inside" data-id="element-3235">
            <li data-id="element-3236">
              All registration information you submit will be true, accurate,
              current, and complete
            </li>
            <li data-id="element-3237">
              You will maintain the accuracy of such information and promptly
              update it as necessary
            </li>
            <li data-id="element-3238">
              You have the legal capacity and agree to comply with these Terms
              of Use
            </li>
            <li data-id="element-3239">You are not a minor in the jurisdiction in which you reside</li>
            <li data-id="element-3240">
              You will not access the site through automated or non-human means
            </li>
            <li data-id="element-3241">
              You will not use the site for any illegal or unauthorized purpose
            </li>
            <li data-id="element-3242">
              Your use of the site will not violate any applicable law or
              regulation
            </li>
          </ul>
        </section>

        {/* Section 4 */}
        <section id="prohibited" className="mt-14 scroll-mt-24" data-id="element-3243">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3244">
            Prohibited Activities
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-3245">
            You may not access or use the site for any purpose other than that
            for which we make the site available. The site may not be used in
            connection with any commercial endeavors except those that are
            specifically endorsed or approved by us.
          </p>
          <p className="text-slate-700 leading-relaxed mb-3" data-id="element-3246">
            As a user of the site, you agree not to:
          </p>
          <ul className="text-slate-700 space-y-2 list-disc list-inside" data-id="element-3247">
            <li data-id="element-3248">
              Systematically retrieve data or other content from the site to
              create a collection, compilation, database, or directory
            </li>
            <li data-id="element-3249">
              Make any unauthorized use of the site, including collecting
              usernames and/or email addresses by electronic or other means
            </li>
            <li data-id="element-3250">
              Use the site to advertise or offer to sell goods and services
            </li>
            <li data-id="element-3251">
              Circumvent, disable, or otherwise interfere with security-related
              features of the site
            </li>
            <li data-id="element-3252">Engage in unauthorized framing of or linking to the site</li>
            <li data-id="element-3253">Trick, defraud, or mislead us and other users</li>
            <li data-id="element-3254">
              Interfere with, disrupt, or create an undue burden on the site or
              the networks or services connected to the site
            </li>
            <li data-id="element-3255">Attempt to impersonate another user or person</li>
            <li data-id="element-3256">
              Use any information obtained from the site to harass, abuse, or
              harm another person
            </li>
            <li data-id="element-3257">
              Use the site in a manner inconsistent with any applicable laws or
              regulations
            </li>
          </ul>
        </section>

        {/* Section 5 */}
        <section id="user-content" className="mt-14 scroll-mt-24" data-id="element-3258">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3259">
            User Generated Contributions
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-3260">
            The site may invite you to chat, contribute to, or participate in
            blogs, message boards, online forums, and other functionality. Any
            content you transmit to the site will be treated as non-confidential
            and non-proprietary.
          </p>
          <p className="text-slate-700 leading-relaxed" data-id="element-3261">
            By posting content to the site, you grant us an unrestricted,
            unlimited, irrevocable, perpetual, non-exclusive, transferable,
            royalty-free, fully-paid, worldwide right to use, copy, reproduce,
            distribute, and display such content.
          </p>
        </section>

        {/* Section 6 */}
        <section id="submissions" className="mt-14 scroll-mt-24" data-id="element-3262">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3263">
            Submissions
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-3264">
            You acknowledge and agree that any questions, comments, suggestions,
            ideas, feedback, or other information regarding the site provided by
            you to us are non-confidential and shall become our sole property.
            We shall own exclusive rights, including all intellectual property
            rights, and shall be entitled to the unrestricted use and
            dissemination of these submissions for any lawful purpose,
            commercial or otherwise, without acknowledgment or compensation to
            you.
          </p>
        </section>

        {/* Section 7 */}
        <section id="management" className="mt-14 scroll-mt-24" data-id="element-3265">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3266">
            Site Management
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-3267">
            We reserve the right, but not the obligation, to:
          </p>
          <ul className="text-slate-700 space-y-2 list-disc list-inside" data-id="element-3268">
            <li data-id="element-3269">Monitor the site for violations of these Terms of Use</li>
            <li data-id="element-3270">
              Take appropriate legal action against anyone who violates the law
              or these Terms of Use
            </li>
            <li data-id="element-3271">
              Refuse, restrict access to, limit availability of, or disable any
              of your contributions
            </li>
            <li data-id="element-3272">
              Remove from the site or otherwise disable all files and content
              that are excessive in size or burdensome to our systems
            </li>
            <li data-id="element-3273">
              Manage the site in a manner designed to protect our rights and
              property and to facilitate the proper functioning of the site
            </li>
          </ul>
        </section>

        {/* Section 8 */}
        <section id="privacy" className="mt-14 scroll-mt-24" data-id="element-3274">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3275">
            Privacy Policy
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-3276">
            We care about data privacy and security. Please review our{' '}
            <a href="/privacy-policy" onClick={e => {
            if (onNavigate) {
              e.preventDefault();
              onNavigate('/privacy-policy');
            }
          }} className="text-amber-600 hover:underline font-medium" data-id="element-3277">
              Privacy Policy
            </a>
            . By using the site, you agree to be bound by our Privacy Policy,
            which is incorporated into these Terms of Use.
          </p>
        </section>

        {/* Section 9 */}
        <section id="sms" className="mt-14 scroll-mt-24" data-id="element-3278">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3279">
            SMS Communications Terms
          </h2>
          <p className="text-slate-700 leading-relaxed mb-6" data-id="element-3280">
            By providing your mobile phone number and submitting a form on our
            site, you agree to the following terms governing SMS/text message
            communications:
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3281">
            Program Description
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3282">
            findservicequotes.io may send you autodialed and/or pre-recorded
            marketing text messages (SMS/MMS) regarding educational programs,
            follow-up inquiries, and promotional offers from us and our
            educational partners.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3283">
            Consent
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3284">
            By submitting your information, you expressly consent to receive
            such messages at the mobile number provided. Consent is not a
            condition of any purchase or service. Your consent is governed by
            our Privacy Policy, which is incorporated herein by reference.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3285">
            Message Frequency
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3286">
            Message frequency varies. You may receive multiple messages per
            inquiry depending on your interactions and program interests.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3287">
            Costs
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3288">
            Message and data rates may apply. Contact your wireless carrier for
            details about your plan.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3289">
            Opt-Out
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3290">
            You may opt out of SMS communications at any time by replying{' '}
            <strong data-id="element-3291">STOP</strong> to any message received. After opting out, you
            will receive a single confirmation message and no further SMS
            marketing messages will be sent unless you provide new consent.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3292">
            Assistance
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3293">
            For help, reply <strong data-id="element-3294">HELP</strong> to any message or contact us
            through the information provided on our website.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3295">
            No Warranty for Delivery
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3296">
            We do not guarantee that SMS messages will be delivered. Delivery is
            subject to effective transmission and your carrier&apos;s coverage
            and policies. We are not liable for delays or failures in SMS
            delivery.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3297">
            Third-Party Carriers
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3298">
            You acknowledge that SMS transmission involves third-party carriers
            over whom we have no control. Standard carrier terms and conditions
            apply.
          </p>
        </section>

        {/* Section 10 */}
        <section id="email" className="mt-14 scroll-mt-24" data-id="element-3299">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3300">
            Email Communications Terms
          </h2>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3301">
            Program Description
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3302">
            findservicequotes.io may send you marketing and promotional emails
            regarding educational programs, follow-up inquiries, and offers from
            us and our educational partners. Marketing emails are sent from{' '}
            <a href="mailto:admin@media-matchers.com" className="text-amber-600 hover:underline" data-id="element-3303">
              admin@media-matchers.com
            </a>{' '}
            on behalf of Media Matchers LLC.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3304">
            Consent
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3305">
            By submitting your information on our site, you expressly consent to
            receive such emails at the address provided. Consent is not a
            condition of any purchase or service.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3306">
            Opt-Out
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3307">
            You may opt out of marketing emails at any time by clicking the
            unsubscribe link included in every marketing email. Opt-out requests
            will be honored within 10 business days. You may also opt out by
            contacting us at the address below.
          </p>

          <h3 className="text-lg font-semibold text-slate-900 mt-6 mb-3" data-id="element-3308">
            CAN-SPAM Compliance
          </h3>
          <p className="text-slate-700 leading-relaxed" data-id="element-3309">
            All commercial emails we send will accurately identify the sender,
            include our physical mailing address, and include a working
            unsubscribe mechanism.
          </p>
        </section>

        {/* Section 11 */}
        <section id="term" className="mt-14 scroll-mt-24" data-id="element-3310">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3311">
            Term and Termination
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4" data-id="element-3312">
            These Terms of Use shall remain in full force and effect while you
            use the site. We reserve the right to deny access to the site to
            anyone for any reason at any time without notice.
          </p>
          <p className="text-slate-700 leading-relaxed" data-id="element-3313">
            If we terminate or suspend your access for any reason, you are
            prohibited from registering and creating a new account under your
            name or a borrowed name. We reserve the right to take appropriate
            legal action, including civil, criminal, and injunctive redress.
          </p>
        </section>

        {/* Section 12 */}
        <section id="disclaimer" className="mt-14 scroll-mt-24" data-id="element-3314">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3315">
            Disclaimer
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4 uppercase text-sm" data-id="element-3316">
            The site is provided on an as-is and as-available basis. You agree
            that your use of the site and our services will be at your sole
            risk. To the fullest extent permitted by law, we disclaim all
            warranties, express or implied, in connection with the site and your
            use thereof.
          </p>
          <p className="text-slate-700 leading-relaxed uppercase text-sm" data-id="element-3317">
            We make no warranties or representations about the accuracy or
            completeness of the site&apos;s content or the content of any
            websites linked to the site and we will assume no liability or
            responsibility for any errors, mistakes, or inaccuracies of content
            and materials.
          </p>
        </section>

        {/* Section 13 */}
        <section id="liability" className="mt-14 scroll-mt-24" data-id="element-3318">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3319">
            Limitations of Liability
          </h2>
          <p className="text-slate-700 leading-relaxed uppercase text-sm" data-id="element-3320">
            In no event will we or our directors, employees, or agents be liable
            to you or any third party for any direct, indirect, consequential,
            exemplary, incidental, special, or punitive damages, including lost
            profit, lost revenue, loss of data, or other damages arising from
            your use of the site.
          </p>
        </section>

        {/* Section 14 */}
        <section id="indemnification" className="mt-14 scroll-mt-24" data-id="element-3321">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3322">
            Indemnification
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-3323">
            You agree to defend, indemnify, and hold us harmless, including our
            subsidiaries, affiliates, and all of our respective officers,
            agents, partners, and employees, from and against any loss, damage,
            liability, claim, or demand, including reasonable attorneys&apos;
            fees and expenses, made by any third party due to or arising out of
            your use of the site or breach of these Terms of Use.
          </p>
        </section>

        {/* Section 15 */}
        <section id="law" className="mt-14 scroll-mt-24" data-id="element-3324">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3325">
            Governing Law
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-3326">
            These Terms of Use and your use of the site are governed by and
            construed in accordance with the laws of the State of California,
            without regard to its conflict of law principles. Except for
            disputes subject to binding arbitration under the Dispute Resolution
            / Arbitration section below, you agree that any dispute arising out
            of or relating to these Terms shall be resolved exclusively in the
            state or federal courts located in Los Angeles County, California.
          </p>
        </section>

        {/* Section 16 */}
        <section id="arbitration" className="mt-14 scroll-mt-24" data-id="element-3327">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3328">
            Dispute Resolution / Arbitration
          </h2>
          <p className="text-slate-700 leading-relaxed" data-id="element-3329">
            Any dispute, claim, or controversy arising out of or relating to
            these Terms or your use of the site, including any claims under the
            Telephone Consumer Protection Act (TCPA), shall be resolved by
            binding individual arbitration under the rules of the American
            Arbitration Association. You waive any right to bring or participate
            in a class action lawsuit or class-wide arbitration. This
            arbitration agreement does not apply to claims that may be brought
            in small claims court.
          </p>
        </section>

        {/* Section 17 */}
        <section id="contact" className="mt-14 scroll-mt-24" data-id="element-3330">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-id="element-3331">
            Contact Us
          </h2>
          <p className="text-slate-700 leading-relaxed mb-6" data-id="element-3332">
            If you have any questions or concerns about these Terms &amp;
            Conditions, please contact us at:
          </p>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8" data-id="element-3333">
            <p className="font-bold text-slate-900 text-lg mb-4" data-id="element-3334">
              Media Matchers LLC
            </p>
            <div className="space-y-3" data-id="element-3335">
              <div className="flex items-start gap-3" data-id="element-3336">
                <MapPinIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-3337" />
                <p className="text-slate-700" data-id="element-3338">
                  1865 Chestnut Street
                  <br data-id="element-3339" />
                  San Francisco, CA 94123
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-3340">
                <MailIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-3341" />
                <a href="mailto:admin@media-matchers.com" className="text-amber-600 hover:underline font-medium" data-id="element-3342">
                  admin@media-matchers.com
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>;
}